import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/auth/AuthShell';
import PasswordField from '../components/auth/PasswordField';
import { errorBoxSx, primaryButtonSx, underlineStyle } from '../components/auth/authStyles';
import { useAuth } from '../contexts/AuthContext';
import UserService from '../services/UserService';
import i18n, { normalizeLang } from '../i18n';

/** Where an invited employee lands from the email/link: confirm their name and pick a password. */
export default function SetupAccountPage() {
    const { t } = useTranslation();
    const { acceptInvite } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const [invite, setInvite] = useState(null);
    const [invalid, setInvalid] = useState(false);
    const [form, setForm] = useState({ firstName: '', lastName: '', password: '', confirm: '' });
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!token) { setInvalid(true); return; }
        UserService.inviteInfo(token).then((info) => {
            setInvite(info);
            setForm(f => ({ ...f, firstName: info.firstName || '', lastName: info.lastName || '' }));
            // Show the page in the language the admin uses (the account starts with it too).
            const lng = normalizeLang(info.language);
            if (lng && lng !== i18n.language) i18n.changeLanguage(lng);
        }).catch(() => setInvalid(true));
    }, [token]);

    const onChange = (e) => {
        setForm(f => ({ ...f, [e.target.name]: e.target.value }));
        if (error) setError(null);
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.firstName.trim() || !form.lastName.trim()) { setError(t('setup.errors.nameRequired')); return; }
        if (form.password.length < 8) { setError(t('auth.register.errors.passwordShort')); return; }
        if (form.password !== form.confirm) { setError(t('auth.register.errors.passwordMismatch')); return; }
        setSaving(true);
        try {
            const data = await acceptInvite({ token, firstName: form.firstName.trim(), lastName: form.lastName.trim(), password: form.password });
            navigate(data.twoFactorRequired ? '/login' : '/dashboard', { replace: true });
        } catch (err) {
            if (err?.response?.data?.code === 'INVITE_INVALID') setInvalid(true);
            else setError(t('common.error'));
            setSaving(false);
        }
    };

    if (invalid) {
        return (
            <AuthShell formMaxWidth={380}>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 1 }}>{t('setup.invalidTitle')}</Typography>
                <Typography variant="body2" sx={{ color: '#7f8c9a', mb: 4 }}>{t('setup.invalidText')}</Typography>
                <Button component={RouterLink} to="/login" fullWidth variant="contained" sx={primaryButtonSx}>{t('auth.register.loginLink')}</Button>
            </AuthShell>
        );
    }
    if (!invite) {
        return <AuthShell formMaxWidth={380}><Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box></AuthShell>;
    }

    return (
        <AuthShell formMaxWidth={380}>
            <Box component="form" onSubmit={submit} noValidate>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>{t('setup.title')}</Typography>
                <Typography variant="body2" sx={{ color: '#7f8c9a', mb: 4 }}>
                    {t('setup.subtitle', { company: invite.companyName, interpolation: { escapeValue: false } })}
                </Typography>

                {error && <Box sx={errorBoxSx}><Typography variant="body2" color="error">{error}</Typography></Box>}

                <TextField fullWidth variant="standard" label={t('auth.fields.email')} value={invite.email} disabled sx={{ mb: 3, ...underlineStyle }} />
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField fullWidth variant="standard" name="firstName" autoComplete="given-name" label={t('auth.fields.firstName')}
                               value={form.firstName} onChange={onChange} sx={underlineStyle} />
                    <TextField fullWidth variant="standard" name="lastName" autoComplete="family-name" label={t('auth.fields.lastName')}
                               value={form.lastName} onChange={onChange} sx={underlineStyle} />
                </Box>
                <PasswordField name="password" autoComplete="new-password" autoFocus label={t('auth.fields.password')}
                               value={form.password} onChange={onChange} helperText={t('auth.register.passwordHint')} sx={{ mb: 3 }} />
                <PasswordField name="confirm" autoComplete="new-password" label={t('auth.fields.confirmPassword')}
                               value={form.confirm} onChange={onChange} sx={{ mb: 4 }} />
                <Button type="submit" fullWidth variant="contained" disabled={saving} sx={primaryButtonSx}>
                    {saving ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('setup.submit')}
                </Button>
                <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: '#7f8c9a' }}>
                    {t('auth.register.haveAccount')}{' '}
                    <Link component={RouterLink} to="/login" sx={{ color: '#427898', fontWeight: 600 }}>{t('auth.register.loginLink')}</Link>
                </Typography>
            </Box>
        </AuthShell>
    );
}
